/*
  theme.js — the storefront behaviour. One module, no dependencies.

  1. State: computes open / closed / teaser / nomenu from the schedule with server time,
     flips the page live at the boundaries (a short cross-fade), fills the status line and
     dates, marks the last three hours (html[data-closing-soon]).
  2. Cart: steppers on cards and in the cart, size pills (Loaf / Slice), the order bar, the
     cart panel (a <dialog> that is modal on phones and a static column on the desktop menu
     page) and the /cart page, the pick-up cart attribute, sold-out and stock limits, and
     what happens when the network or Shopify says no.
  3. Small things: info sheet, photo dots, the doors intro, screen-reader announcements.

  Markup contract (data attributes):
    [data-mm="status|menu-date-long|menu-date-short|next-open|pickup-window|closes-at"] text slots
    [data-stepper data-variant-id data-max data-qty] with [data-action="add|inc|dec"] and <output>
    [data-line-key] on cart line steppers; [data-set-line data-qty] sets a line directly
    [data-product] with [data-variant-picker] radios, [data-price], [data-left]
    [data-open-cart] [data-close-cart] #cart-dialog [data-order-bar] [data-cart-lines]
    [data-cart-page=section id] on the /cart page; [data-swipe] + [data-dots] photo strips
*/
import {
  shopState, fmtLongDate, fmtShortDate, fmtWeekdayTime, fmtCountdown, dateNoon, pickupStillValid, HOUR,
} from '@mm/state';

const doc = document.documentElement;
const data = JSON.parse(document.getElementById('mm-schedule').textContent);
const S = data.strings || {};
// The t filter HTML-escapes ("Couldn&#39;t"); these strings go into textContent, so decode once.
{
  const el = document.createElement('textarea');
  for (const [k, v] of Object.entries(S)) if (typeof v === 'string' && v.includes('&')) { el.innerHTML = v; S[k] = el.value; }
}
{
  const forced = (data.designMode || data.themeRole !== 'main') && /[?&]mm_state=(open|closed|teaser|nomenu)/.exec(location.search);
  if (forced) data.previewState = forced[1];
}
const live = document.getElementById('mm-live');

let clockOffset = 0; // serverNow − Date.now()
const now = () => Date.now() + clockOffset;
const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
const fill = (tpl, vars) => Object.entries(vars).reduce((t, [k, v]) => t.replace(`{{ ${k} }}`, v), tpl || '');

function announce(text) {
  if (!live || !text) return;
  live.textContent = '';
  requestAnimationFrame(() => { live.textContent = text; });
}

/** Runs a WAAPI animation unless the visitor asked for less motion. */
function motion(el, frames, opts) {
  if (!el || reduceMotion.matches || !el.animate) return null;
  return el.animate(frames, { easing: 'cubic-bezier(0.16, 1, 0.3, 1)', ...opts });
}

/* ------------------------------------------------------------------ state */

let lastState = doc.dataset.state;
let stateTimer = 0;

function currentState() {
  const st = shopState(now(), data);
  if (data.previewState) {
    st.state = data.previewState;
    st.preview = true;
  }
  return st;
}

/** "Monday 12:00" within the week, "Monday 26.10 at 12:00" further out. */
function fmtNextOpen(ms) {
  const far = ms - now() > 6 * 24 * HOUR;
  return far ? `${fmtWeekdayTime(ms, S).replace(' ', ` ${fmtShortDate(ms)} ${S.at || 'at'} `)}` : fmtWeekdayTime(ms, S);
}

function fillSlot(slot, text) {
  document.querySelectorAll(`[data-mm="${slot}"]`).forEach((el) => {
    if (el.textContent !== text) el.textContent = text;
  });
}

function applyState() {
  const st = currentState();
  const changed = lastState && lastState !== 'unknown' && lastState !== st.state;

  const write = () => {
    doc.dataset.state = st.state;
    doc.dataset.menuPast = String(st.menuPast);
  };
  // A live flip at a boundary cross-fades instead of jumping (the first paint never does).
  if (changed && document.startViewTransition && !reduceMotion.matches && !document.hidden) document.startViewTransition(write);
  else write();

  // Dates
  const menuNoon = dateNoon(st.menuDate);
  if (menuNoon) {
    fillSlot('menu-date-long', fmtLongDate(menuNoon, S));
    fillSlot('menu-date-short', fmtShortDate(menuNoon));
  }
  if (st.nextOpenAt) fillSlot('next-open', fmtNextOpen(st.nextOpenAt));
  if (st.closeAt) fillSlot('closes-at', fmtWeekdayTime(st.closeAt, S));
  fillSlot('pickup-window', `${data.pickup.from}–${data.pickup.until}`);

  // Status line
  let status = '';
  let tick = 0;
  let soon = false;
  if (st.state === 'open' && st.closeAt) {
    const left = st.closeAt - now();
    if (left <= 3 * HOUR && !st.preview) {
      status = S.closesIn.replace('{{ time }}', fmtCountdown(left, S));
      tick = 30000;
      soon = true;
    } else {
      status = S.open.replace('{{ time }}', fmtWeekdayTime(st.closeAt, S));
    }
  } else if (st.state === 'teaser' && st.nextOpenAt) {
    const until = st.nextOpenAt - now();
    if (until <= 3 * HOUR && !st.preview) {
      status = S.opensIn.replace('{{ time }}', fmtCountdown(until, S));
      tick = 30000;
    } else {
      status = S.opensAt.replace('{{ time }}', fmtNextOpen(st.nextOpenAt));
    }
  } else if (st.nextOpenAt) {
    status = S.closed.replace('{{ time }}', fmtNextOpen(st.nextOpenAt));
  }
  if (status) fillSlot('status', status);
  if (soon) doc.dataset.closingSoon = 'true';
  else delete doc.dataset.closingSoon;

  if (changed) {
    announce(status);
    if (st.state !== 'open') Cart.onClosed();
  }
  lastState = st.state;

  // Next boundary
  clearTimeout(stateTimer);
  if (st.preview) return;
  const boundaries = [st.openAt, st.closeAt, st.menuEnd, st.nextOpenAt].filter((t) => t && t > now());
  let wait = boundaries.length ? Math.min(...boundaries) - now() + 500 : 6 * HOUR;
  if (tick) wait = Math.min(wait, tick);
  stateTimer = setTimeout(applyState, Math.min(Math.max(wait, 1000), 2147000000));
}

async function syncClock() {
  try {
    const res = await fetch(`${data.cartUrl}.js`, { cache: 'no-store', headers: { Accept: 'application/json' } });
    const serverDate = res.headers.get('date');
    if (serverDate) clockOffset = new Date(serverDate).getTime() + 500 - Date.now();
    const cart = await res.json();
    Cart.sync(cart);
  } catch (e) {
    /* offline: keep device clock */
  }
  applyState();
}

/* ------------------------------------------------------------------- cart */

class CartError extends Error {}

/** Cents → "€15,00" in the shop's own money format. */
function money(cents) {
  const fmt = data.moneyFormat || '€{{amount_with_comma_separator}}';
  const n = (cents / 100).toFixed(2);
  const [int, dec] = n.split('.');
  const dots = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const commas = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
    amount: `${commas}.${dec}`,
    amount_no_decimals: commas,
    amount_with_comma_separator: `${dots},${dec}`,
    amount_no_decimals_with_comma_separator: dots,
  }[k] ?? `${dots},${dec}`));
}
const itemsLabel = (n) => fill(n === 1 ? S.itemsOne : S.itemsOther, { count: n }) || `${n}`;

/*
  The cart feels instant: every tap updates the stepper, the bag count, the pill badges and
  the order bar on the same frame (from the price on the stepper). Taps on one bake are
  coalesced (160 ms) into one /cart/update.js call that returns the cart and the re-rendered
  sections together; requests run one at a time, so the last answer is always the truth.
*/
const Cart = {
  items: new Map(), // variantId → { qty, key, price } as Shopify last said
  want: new Map(),  // variantId → a change still on its way { qty, price, stepper, before, waiters, timer, inflight, again }
  count: null,
  pending: 0,
  queue: Promise.resolve(),
  attrsDone: false,
  slotPending: null, // a pick-up slot just chosen, until Shopify has echoed it back

  /** The quantity the visitor sees: Shopify's, or the change still on its way. */
  qty(id) {
    const w = this.want.get(String(id));
    return w ? w.qty : (this.items.get(String(id))?.qty || 0);
  },

  totals() {
    let count = 0;
    let total = 0;
    new Set([...this.items.keys(), ...this.want.keys()]).forEach((id) => {
      const q = this.qty(id);
      const price = this.want.get(id)?.price || this.items.get(id)?.price || 0;
      count += q;
      total += q * price;
    });
    return { count, total };
  },

  idle() { return !this.pending && !this.want.size; },

  busy(delta) {
    this.pending += delta;
    if (this.pending > 0 || this.want.size) doc.dataset.cartBusy = 'true';
    else delete doc.dataset.cartBusy;
  },

  sync(cart) {
    this.items.clear();
    (cart.items || []).forEach((it) => {
      const id = String(it.variant_id);
      const prev = this.items.get(id);
      this.items.set(id, { qty: (prev ? prev.qty : 0) + it.quantity, key: it.key, price: it.final_price ?? it.price });
    });
    this.reflect();
    this.ensureAttributes(cart);
  },

  reflect() {
    const ready = this.count !== null; // the first fill after a page load stays still
    document.querySelectorAll('[data-stepper]').forEach((el) => {
      if (el.dataset.lineKey) return; // cart lines re-render from the server
      setStepper(el, this.qty(el.dataset.variantId));
    });
    document.querySelectorAll('[data-variant-picker] input').forEach((input) => {
      const badge = input.closest('label')?.querySelector('[data-pill-qty]');
      if (!badge) return;
      const qty = this.qty(input.value);
      const wasHidden = badge.hidden;
      badge.textContent = qty;
      badge.hidden = qty === 0;
      if (ready && wasHidden && !badge.hidden) motion(badge, [{ transform: 'scale(0.6)', opacity: 0 }, { transform: 'none', opacity: 1 }], { duration: 220 });
    });
    const { count, total } = this.totals();
    const before = this.count;
    this.count = count;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      const wasHidden = el.hidden;
      el.textContent = count;
      el.hidden = count === 0;
      // the bag's count pops in when it appears and gives a small "it changed" after that
      if (!ready || el.hidden) return;
      if (wasHidden) motion(el, [{ transform: 'scale(0.6)' }, { transform: 'none' }], { duration: 220 });
      else if (before !== count) motion(el, [{ transform: 'scale(1)' }, { transform: 'scale(1.3)' }, { transform: 'scale(1)' }], { duration: 320 });
    });
    const bar = document.querySelector('[data-order-bar]');
    if (bar) {
      showBar(bar, count > 0, ready);
      const c = bar.querySelector('[data-order-count]');
      const t = bar.querySelector('[data-order-total]');
      const label = itemsLabel(count);
      if (c && c.textContent !== label) c.textContent = label;
      if (t && t.textContent !== money(total)) { t.textContent = money(total); if (before) bump(t); }
    }
  },

  sections() {
    const page = document.querySelector('[data-cart-page]');
    return ['cart-panel', page && page.dataset.cartPage].filter(Boolean).join(',');
  },

  /** One cart request at a time, in order. Resolves with Shopify's JSON (sections included). */
  post(path, body) {
    const run = this.queue.then(async () => {
      this.busy(1);
      try {
        let res;
        try {
          res = await fetch(`${data.cartUrl}${path}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ ...body, sections: this.sections(), sections_url: location.pathname }),
          });
        } catch (e) {
          throw Object.assign(new CartError('network'), { network: true });
        }
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw Object.assign(new CartError(json.description || json.message || 'Cart error'), { status: res.status, json });
        return json;
      } finally {
        this.busy(-1);
      }
    });
    this.queue = run.catch(() => {});
    return run;
  },

  /** Re-reads the cart and re-renders the panel; false when the shop can't be reached. */
  async refresh() {
    try {
      const cart = await fetch(`${data.cartUrl}.js`, { cache: 'no-store' }).then((r) => r.json());
      this.sync(cart);
      const html = await fetch(`${location.pathname}?sections=${this.sections()}`).then((r) => r.json()).catch(() => null);
      if (html) renderSections(html);
      return true;
    } catch (e) {
      return false;
    }
  },

  /** Set an absolute quantity for a variant (0 removes). Resolves true when it went through. */
  setQuantity(variantId, qty, stepper) {
    const id = String(variantId);
    const st = currentState();
    if (st.state !== 'open' && qty > 0 && !data.designMode) {
      hint(stepper, S.closed.replace('{{ time }}', st.nextOpenAt ? fmtWeekdayTime(st.nextOpenAt, S) : ''));
      return Promise.resolve(false);
    }
    const max = Number(stepper?.dataset.max ?? Infinity);
    const wanted = Math.max(0, Math.min(qty, max));
    let w = this.want.get(id);
    if (!w) {
      w = { waiters: [], before: this.qty(id) };
      this.want.set(id, w);
    }
    w.qty = wanted;
    w.stepper = stepper || w.stepper;
    w.price = Number(stepper?.dataset.priceCents) || this.items.get(id)?.price || 0;
    if (stepper?.dataset.lineKey) setStepper(stepper, wanted);
    if (wanted > 0 && !this.items.has(id) && !w.thumbed) { w.thumbed = true; addThumb(stepper); }
    this.reflect();
    this.busy(0);
    hint(stepper, wanted > 0 && wanted >= max && max !== Infinity ? (S.allOfThem || "that's all of them") : '');

    return new Promise((resolve) => {
      w.waiters.push(resolve);
      clearTimeout(w.timer);
      if (w.inflight) w.again = true;
      else w.timer = setTimeout(() => this.flush(id), 160);
    });
  },

  async flush(id) {
    const w = this.want.get(id);
    if (!w) return;
    w.inflight = true;
    w.again = false;
    const wanted = w.qty;
    let json = null;
    let err = null;
    try {
      json = await this.post('/update.js', { updates: { [id]: wanted } });
    } catch (e) {
      err = e;
    }
    w.inflight = false;
    // more taps landed while this one was on its way: send the latest, keep the screen as is
    if (!err && (w.again || w.qty !== wanted)) {
      this.flush(id);
      return;
    }
    this.want.delete(id);
    let ok;
    if (err) {
      ok = await this.fail(id, err, w.stepper);
    } else {
      this.sync(json);
      if (json.sections) renderSections(json.sections);
      ok = this.settle(id, wanted, w);
    }
    this.busy(0);
    w.waiters.forEach((resolve) => resolve(ok));
  },

  /** Shopify answered: compare with what was asked. */
  settle(id, wanted, w) {
    const got = this.items.get(id)?.qty || 0;
    const stepper = w.stepper;
    if (stepper && got < wanted) {
      // Shopify gave fewer than asked: that's the stock now.
      if (got === 0 && wanted > 0) markSoldOut(stepper);
      else limitStepper(stepper, got);
    }
    if (wanted > w.before && got > w.before) {
      announce(S.added || 'Added to your order');
      document.dispatchEvent(new CustomEvent('mm:added', { detail: { id, stepper } }));
    }
    return got > 0 || wanted === 0;
  },

  async fail(id, err, stepper) {
    const reached = await this.refresh();
    const nowQty = this.items.get(id)?.qty || 0;
    if (stepper) setStepper(stepper, nowQty); // roll back the optimistic number
    if (err.network || !reached) {
      hint(stepper, S.network);
      announce(S.network);
    } else if (err.status === 422) {
      // "… is already sold out." / "You can only add 4 …" / "All 4 … are in your cart."
      const msg = err.message || '';
      const n = /sold out/i.test(msg) ? 0 : Number((/(?:only add|all)\s+(\d+)/i.exec(msg) || [])[1] ?? nowQty);
      if (n === 0 && nowQty === 0) markSoldOut(stepper);
      else limitStepper(stepper, Math.max(n, nowQty));
    } else {
      hint(stepper, S.cantAdd);
      announce(S.cantAdd);
    }
    return false;
  },

  /** Cart lines: set a line by key (sold-out "Remove", over-quantity "Set to n"). */
  async setLine(key, qty) {
    try {
      const json = await this.post('/change.js', { id: key, quantity: qty });
      this.sync(json);
      if (json.sections) renderSections(json.sections);
    } catch (err) {
      if (!(await this.refresh())) announce(S.network);
    }
  },

  /** Writes the pick-up facts onto the cart so they travel to the order and the emails. */
  async ensureAttributes(cart) {
    if (this.attrsDone || !cart || !cart.item_count) return;
    const st = currentState();
    const noon = dateNoon(st.menuDate);
    if (!noon) return;
    const day = fmtLongDate(noon, S);
    const attrs = cart.attributes || {};
    const current = attrs[S.attrPickup];
    // a slot chosen for this menu (or the whole window) stays; last week's, or a slot that no longer exists, resets to "not chosen yet"
    const pickup = pickupStillValid(current, day, data.pickup, data.pickupStep)
      ? current
      : `${day}, ${data.pickup.from}–${data.pickup.until}`;
    if (current === pickup && attrs['Menu date'] === st.menuDate) { this.attrsDone = true; return; }
    this.attrsDone = true;
    // through the queue, so it never races a quantity change; its answer isn't needed
    this.queue = this.queue.then(() => fetch(`${data.cartUrl}/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [S.attrPickup]: pickup, 'Menu date': st.menuDate } }),
    })).catch(() => { this.attrsDone = false; });
  },

  onClosed() {
    document.querySelectorAll('[data-stepper]').forEach((el) => hint(el, ''));
  },
};

/** The order bar rises in with the first bake and slides away when the order empties. */
function showBar(bar, show, animate) {
  const btn = bar.querySelector('.order-bar__btn');
  const leaving = bar.classList.contains('is-leaving');
  if (show && (bar.hidden || leaving)) {
    bar.classList.remove('is-leaving');
    btn?.getAnimations().forEach((a) => a.cancel());
    bar.hidden = false;
    if (animate) motion(btn, [{ transform: 'translateY(200%)' }, { transform: 'none' }], { duration: 400 });
  } else if (!show && !bar.hidden && !leaving) {
    const a = animate ? motion(btn, [{ transform: 'none', opacity: 1 }, { transform: 'translateY(200%)', opacity: 0 }], { duration: 240, easing: 'cubic-bezier(0.5, 0, 0.75, 0)', fill: 'forwards' }) : null;
    if (!a) { bar.hidden = true; return; }
    bar.classList.add('is-leaving');
    a.finished.then(() => {
      if (!bar.classList.contains('is-leaving')) return;
      bar.classList.remove('is-leaving');
      bar.hidden = true;
      a.cancel();
    }, () => {});
  }
}

/** The bake's first photo, as shown on its card or product page. */
function photoOf(stepper) {
  const img = stepper?.closest('[data-product]')?.querySelector('.card__photo--1 img, .pdp__photo img');
  return img ? (img.currentSrc || img.src) : '';
}

/** A new bake joins the pile on the order bar before Shopify has answered. */
function addThumb(stepper) {
  const pile = document.querySelector('[data-order-thumbs]');
  const src = photoOf(stepper);
  if (!pile || !src) return;
  const span = document.createElement('span');
  span.className = 'order-bar__thumb';
  const img = new Image();
  img.src = src;
  img.alt = '';
  span.append(img);
  pile.prepend(span);
  while (pile.children.length > 3) pile.lastElementChild.remove();
  motion(span, [{ transform: 'translateY(8px) scale(0.8)', opacity: 0 }, { transform: 'none', opacity: 1 }], { duration: 260 });
}

function setStepper(el, qty) {
  const prev = Number(el.dataset.qty || 0);
  el.dataset.qty = qty;
  const out = el.querySelector('output');
  if (out && out.value !== String(qty)) {
    out.value = qty;
    // the number rolls the way it went
    if (prev > 0 && qty > 0) motion(out, [{ transform: `translateY(${qty > prev ? 6 : -6}px)`, opacity: 0 }, { transform: 'none', opacity: 1 }], { duration: 180 });
  }
  const max = Number(el.dataset.max ?? Infinity);
  const inc = el.querySelector('[data-action="inc"]');
  if (inc) inc.disabled = qty >= max;
  const add = el.querySelector('[data-action="add"]');
  if (add) add.disabled = max <= 0;
  const wasActive = el.classList.contains('is-active');
  el.classList.toggle('is-active', qty > 0);
  // keyboard: Add disappears under the focus, so hand it to + (and back to Add at zero)
  if (!wasActive && qty > 0 && document.activeElement === add) inc?.focus({ preventScroll: true });
  if (wasActive && qty === 0 && el.contains(document.activeElement) && add) add.focus({ preventScroll: true });
}

function hint(el, text) {
  const h = el?.querySelector('[data-hint]');
  if (!h) return;
  h.textContent = text || '';
  h.hidden = !text;
}

/** The stock turned out to be n: cap the stepper and say so. */
function limitStepper(stepper, n) {
  if (!stepper) return;
  stepper.dataset.max = String(n);
  const card = stepper.closest('[data-product]');
  const pill = card?.querySelector(`[data-variant-picker] input[value="${stepper.dataset.variantId}"]`);
  if (pill) pill.dataset.max = String(n);
  setStepper(stepper, Number(stepper.dataset.qty || 0));
  hint(stepper, Number(stepper.dataset.qty || 0) >= n ? (S.allOfThem || "that's all of them") : fill(S.onlyLeft, { count: n }));
  updateLeft(card, n);
}

/** Sold out while the page was open: the same treatment the server renders. */
function markSoldOut(stepper) {
  if (!stepper) return;
  const card = stepper.closest('[data-product]');
  stepper.dataset.max = '0';
  setStepper(stepper, 0);
  hint(stepper, '');
  const pill = card?.querySelector(`[data-variant-picker] input[value="${stepper.dataset.variantId}"]`);
  if (pill) {
    pill.dataset.max = '0';
    pill.dataset.available = 'false';
    pill.closest('.variant-pill')?.classList.add('is-soldout');
    const all = [...card.querySelectorAll('[data-variant-picker] input')];
    if (all.every((i) => i.dataset.available !== 'true')) card.dataset.soldout = 'true';
    else selectVariant(card, pill);
  } else if (card) {
    card.dataset.soldout = 'true';
  }
  motion(card?.querySelector('.card__flag--soldout, .pdp__soldout'), [{ transform: 'scale(0.6)', opacity: 0 }, { transform: 'none', opacity: 1 }], { duration: 260 });
  announce(S.soldOut || 'Sold out');
}

/** "only n left" under a card / on the product page. */
function updateLeft(card, n) {
  const el = card?.querySelector('[data-left]');
  if (!el) return;
  const low = data.lowStock > 0 && n > 0 && n <= data.lowStock;
  el.hidden = !low;
  if (low) el.textContent = fill(S.left, { count: n });
}

/* -------------------------------------------------------------- variants */

/** A size pill was picked: point the stepper, price, stock line and form at that variant. */
function selectVariant(card, input) {
  if (!card || !input) return;
  const stepper = card.querySelector('[data-stepper]:not([data-line-key])');
  const max = Number(input.dataset.max || 0);
  const available = input.dataset.available === 'true' && max > 0;
  input.checked = true;

  const price = card.querySelector('[data-price]');
  if (price && price.textContent !== input.dataset.variantPrice) {
    price.textContent = input.dataset.variantPrice;
    motion(price, [{ opacity: 0.2 }, { opacity: 1 }], { duration: 220 });
  }
  if (stepper) {
    stepper.dataset.variantId = input.value;
    stepper.dataset.max = String(max);
    if (input.dataset.priceCents) stepper.dataset.priceCents = input.dataset.priceCents;
    hint(stepper, '');
    setStepper(stepper, Cart.qty(input.value));
  }
  const formId = card.querySelector('form[data-product-form] [name="id"]');
  if (formId) formId.value = input.value;

  if (available) delete card.dataset.vsoldout;
  else card.dataset.vsoldout = 'true';
  const note = card.querySelector('[data-vsoldout-note]');
  if (note) note.textContent = fill(S.variantSoldOut, { variant: input.dataset.title });
  updateLeft(card, available ? max : 0);
  showVariantMedia(card, input);

  // the product page keeps ?variant= in the address, so a shared link opens the same size
  if (card.matches('.pdp')) {
    const url = new URL(location.href);
    url.searchParams.set('variant', input.value);
    history.replaceState(history.state, '', url);
  }
}

/**
 * The picked size's photos (Loaf → whole, Slice → cut), when the sizes have variant images.
 * A size owns its photo and the ones after it, up to the next size's photo.
 * Cards swap in that size's pair (from its <template>); the product page scrolls its strip to
 * the first one (phones) or moves the group to the top (desktop). One-way: swiping never
 * changes the size.
 */
function showVariantMedia(card, input, instant) {
  if (!card || !input?.dataset.mediaPos) return;
  const tpl = card.querySelector(`template[data-photos-for="${input.value}"]`);
  const strip = card.querySelector('[data-swipe]');
  if (!strip) return;
  if (tpl) {
    if (strip.dataset.photosFor === input.value || (!strip.dataset.photosFor && input.defaultChecked)) return;
    strip.dataset.photosFor = input.value;
    strip.replaceChildren(tpl.content.cloneNode(true));
    strip.scrollLeft = 0;
    card.classList.toggle('card--two', strip.children.length > 1);
    card.querySelectorAll('[data-dots] i').forEach((d, i) => d.classList.toggle('is-on', i === 0));
    if (!instant) motion(strip, [{ opacity: 0.4 }, { opacity: 1 }], { duration: 240 });
    return;
  }
  if (!card.matches('.pdp')) return;
  const photos = [...strip.children];
  const starts = [...card.querySelectorAll('[data-variant-picker] input')].map((i) => Number(i.dataset.mediaPos));
  const from = Number(input.dataset.mediaPos);
  const to = Math.min(photos.length + 1, ...starts.filter((s) => s > from));
  const group = photos.slice(from - 1, to - 1);
  if (!group.length) return;
  if (strip.scrollWidth > strip.clientWidth + 1) {
    strip.scrollTo({ left: group[0].offsetLeft - photos[0].offsetLeft, behavior: instant || reduceMotion.matches ? 'auto' : 'smooth' });
  } else {
    const lead = from > 1 ? group : [];
    if (photos.every((p) => p.classList.contains('is-lead') === lead.includes(p))) return;
    photos.forEach((p) => p.classList.toggle('is-lead', lead.includes(p)));
    if (!instant) group.forEach((p) => motion(p, [{ opacity: 0.3 }, { opacity: 1 }], { duration: 260 }));
  }
}
document.querySelectorAll('[data-variant-picker] input[data-media-pos]:checked').forEach((i) => showVariantMedia(i.closest('[data-product]'), i, true));

document.addEventListener('change', (e) => {
  const input = e.target.closest('[data-variant-picker] input');
  if (input) selectVariant(input.closest('[data-product]'), input);
});

// A pick-up slot was picked: it goes onto the cart at once, so the panel and the /cart page
// agree and every re-render keeps the pill checked (the Liquid reads cart.attributes).
document.addEventListener('change', async (e) => {
  const input = e.target.closest('[data-slot]');
  if (!input || !input.checked) return;
  const form = input.form;
  form?.querySelectorAll('[data-slot-needed]').forEach((m) => { m.hidden = true; });
  const btn = form?.querySelector('[data-checkout]');
  if (btn && !btn.disabled) btn.removeAttribute('aria-describedby');
  // say it back at once; the re-render from Liquid repeats the same line
  const summary = input.closest('[data-pickup-slots]')?.querySelector('[data-slot-summary]');
  if (summary && S.slotPicked) {
    summary.textContent = S.slotPicked.replace('{{ from }}', input.dataset.from || '').replace('{{ until }}', input.dataset.until || '');
    summary.setAttribute('data-picked', '');
  }
  Cart.slotPending = input.value;
  try {
    const json = await Cart.post('/update.js', { attributes: { [S.attrPickup]: input.value } });
    if ((json.attributes || {})[S.attrPickup] === Cart.slotPending) Cart.slotPending = null;
    Cart.sync(json);
    if (json.sections) renderSections(json.sections);
  } catch (err) {
    Cart.slotPending = null;
    if (!(await Cart.refresh())) announce(S.network);
  }
});

/* ------------------------------------------------------- cart re-rendering */

/** Section Rendering API results → the panel, the order bar and (on /cart) the page. */
async function renderSections(sections) {
  if (sections['cart-panel']) await renderPanel(sections['cart-panel']);
  const page = document.querySelector('[data-cart-page]');
  if (page && sections[page.dataset.cartPage]) {
    const tpl = parse(sections[page.dataset.cartPage]);
    await swapLines(page.querySelector('[data-cart-lines]'), tpl.querySelector('[data-cart-lines]'));
  }
  applyState(); // re-fill date and status slots inside the new markup
}

// The markup comes from our own sections, rendered by Shopify's Section Rendering API on the
// same origin (never user input), so swapping it in is the intended pattern.
function parse(html) {
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  return tpl.content;
}

async function renderPanel(html) {
  const next = parse(html);
  const nextBar = next.querySelector('[data-order-bar]');
  const bar = document.querySelector('[data-order-bar]');
  if (nextBar && bar && Cart.idle()) {
    if (!bar.classList.contains('is-leaving')) bar.hidden = nextBar.hidden;
    for (const sel of ['[data-order-count]', '[data-order-total]']) {
      const a = bar.querySelector(sel);
      const b = nextBar.querySelector(sel);
      if (a && b && a.textContent !== b.textContent) a.textContent = b.textContent;
    }
    const pile = bar.querySelector('[data-order-thumbs]');
    const nextPile = nextBar.querySelector('[data-order-thumbs]');
    const srcs = (el) => [...el.querySelectorAll('img')].map((i) => (i.getAttribute('src') || '').split('?')[0].replace(/_\d+x\d*(?=\.)/, ''));
    if (pile && nextPile && srcs(pile).join() !== srcs(nextPile).join()) pile.innerHTML = nextPile.innerHTML;
  }
  await swapLines(document.querySelector('#cart-dialog [data-cart-lines]'), next.querySelector('[data-cart-lines]'));
  const nextTitle = next.querySelector('[data-cart-title]');
  const title = document.querySelector('#cart-dialog [data-cart-title]');
  if (nextTitle && title) title.textContent = nextTitle.textContent;
}

/**
 * Swaps the order lines: a removed line folds away first, a new one slides in, the total
 * nudges when it changes, and keyboard focus stays on the same control.
 */
async function swapLines(box, nextBox) {
  if (!box || !nextBox) return;
  const visible = box.offsetParent !== null;
  const lineKeys = (root) => new Set([...root.querySelectorAll('.cart-line')].map((li) => li.dataset.key));
  const oldKeys = lineKeys(box);
  const newKeys = lineKeys(nextBox);
  const focus = focusSignature(box);
  const oldTotal = box.querySelector('.cart-lines__total .price')?.textContent;

  if (visible && !reduceMotion.matches) {
    const leaving = [...box.querySelectorAll('.cart-line')].filter((li) => !newKeys.has(li.dataset.key));
    await Promise.all(leaving.map((li) => {
      const a = motion(li, [
        { opacity: 1, height: `${li.offsetHeight}px` },
        { opacity: 0, height: '0px', paddingBottom: '0px', borderBottomWidth: '0px' },
      ], { duration: 200, easing: 'cubic-bezier(0.5, 0, 0.75, 0)', fill: 'forwards' });
      li.style.overflow = 'hidden';
      return a ? a.finished.catch(() => {}) : null;
    }));
  }

  box.innerHTML = nextBox.innerHTML;
  // an older answer may still show the previous slot: the one just picked wins
  if (Cart.slotPending) {
    const pick = [...box.querySelectorAll('[data-slot]')].find((i) => i.value === Cart.slotPending);
    if (pick) pick.checked = true;
  }

  if (visible) {
    if (oldKeys.size) {
      box.querySelectorAll('.cart-line').forEach((li) => {
        if (!oldKeys.has(li.dataset.key)) motion(li, [{ opacity: 0, transform: 'translateY(-6px)' }, { opacity: 1, transform: 'none' }], { duration: 260 });
      });
    }
    const total = box.querySelector('.cart-lines__total .price');
    if (total && oldTotal && total.textContent !== oldTotal) bump(total);
  }
  restoreFocus(box, focus);
}

function focusSignature(box) {
  const el = document.activeElement;
  if (!el || !box.contains(el)) return null;
  return {
    key: el.closest('.cart-line')?.dataset.key,
    action: el.dataset.action || (el.matches('[data-set-line]') ? 'set-line' : null),
    checkout: el.matches('[data-checkout]'),
    slot: el.matches('[data-slot]') ? el.value : null,
  };
}

function restoreFocus(box, sig) {
  if (!sig) return;
  let target = null;
  if (sig.checkout) target = box.querySelector('[data-checkout]');
  else if (sig.slot) target = [...box.querySelectorAll('[data-slot]')].find((i) => i.value === sig.slot);
  else if (sig.key) {
    const li = box.querySelector(`.cart-line[data-key="${CSS.escape(sig.key)}"]`);
    if (li) {
      const same = li.querySelector(sig.action === 'set-line' ? '[data-set-line]' : `[data-action="${sig.action}"]`);
      target = same && !same.disabled && same.offsetParent ? same : li.querySelector('[data-action]:not(:disabled), [data-set-line]');
    }
  }
  // the line is gone: the next line, else the checkout, else the panel's close button
  target ||= box.querySelector('.cart-line [data-action]:not(:disabled)') || box.querySelector('[data-checkout]:not(:disabled)')
    || box.closest('dialog')?.querySelector('[data-close-cart]');
  target?.focus({ preventScroll: true });
}

/** A small nudge that says "this number changed". */
function bump(el) {
  motion(el, [{ transform: 'translateY(5px)', opacity: 0.3 }, { transform: 'none', opacity: 1 }], { duration: 260 });
}

/** Close a modal dialog after its leave animation (CSS: .is-closing). */
function closeAnimated(d) {
  if (!d || !d.open || d.classList.contains('is-closing')) return;
  const inner = d.firstElementChild;
  // a sheet that was pulled down leaves from where the finger let go (CSS sheet-down has no from)
  const done = () => { d.classList.remove('is-closing'); d.close(); inner?.style.removeProperty('transform'); d.style.removeProperty('--drag-fade'); };
  if (reduceMotion.matches) { done(); return; }
  d.classList.add('is-closing');
  if (inner && inner.getAnimations) {
    requestAnimationFrame(() => Promise.all(inner.getAnimations().map((a) => a.finished)).then(done, done));
  } else done();
}

/* ------------------------------------------------------------ cart dialog */

const dialog = document.getElementById('cart-dialog');
const desktopMenu = matchMedia('(min-width: 1024px)');
let opener = null;

function isSlipLayout() {
  return desktopMenu.matches && document.body.classList.contains('template-index');
}

function openCart(trigger) {
  if (!dialog) return;
  if (isSlipLayout()) {
    dialog.querySelector('.cart-panel__content')?.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
    dialog.querySelector('[data-checkout]')?.focus({ preventScroll: true });
    return;
  }
  opener = trigger || document.activeElement;
  if (!dialog.open) dialog.showModal();
}

function closeCart() {
  if (!dialog || !dialog.open || isSlipLayout()) return;
  closeAnimated(dialog);
}

function layoutDialog() {
  if (!dialog) return;
  if (isSlipLayout()) {
    if (dialog.open) dialog.close();
    dialog.setAttribute('data-slip', 'true');
    const active = document.activeElement;
    if (!dialog.open) dialog.show();
    // show() focuses the first button; the slip is part of the page, so give focus back.
    if (active && active !== document.body) active.focus({ preventScroll: true });
    else if (dialog.contains(document.activeElement)) document.activeElement.blur();
  } else {
    dialog.removeAttribute('data-slip');
    if (dialog.open) dialog.close();
  }
}

if (dialog) {
  dialog.addEventListener('close', () => {
    if (opener && document.contains(opener)) opener.focus();
    opener = null;
  });
  // A tap on the backdrop lands on the <dialog> itself (the sheet is its child). Checking the
  // target, not coordinates, keeps keyboard "clicks" (Enter on a button, reported at 0,0) inside.
  dialog.addEventListener('click', (e) => {
    if (dialog.hasAttribute('data-slip') || e.target !== dialog) return;
    closeCart();
  });
  desktopMenu.addEventListener('change', layoutDialog);
  dialog.addEventListener('cancel', (e) => { e.preventDefault(); closeCart(); });
  layoutDialog();
}

/* --------------------------------------------- "added" note (desktop) */

// Desktop pages without the order slip: a small note drops from the bag after an add.
// Phones have the order bar; the desktop menu has the slip, so neither needs it.
const pop = document.querySelector('[data-added-pop]');
let popTimer = 0;
function hidePop() {
  if (!pop || pop.hidden || pop.classList.contains('is-leaving')) return;
  pop.classList.add('is-leaving');
  const done = () => { pop.hidden = true; pop.classList.remove('is-leaving'); };
  const a = motion(pop, [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(-6px)' }], { duration: 180, easing: 'cubic-bezier(0.5, 0, 0.75, 0)' });
  if (a) a.finished.then(done, done); else done();
}
function armPop() { clearTimeout(popTimer); popTimer = setTimeout(hidePop, 2800); }
if (pop) {
  document.addEventListener('mm:added', (e) => {
    if (!desktopMenu.matches || isSlipLayout() || (dialog && dialog.open)) return;
    const card = e.detail.stepper?.closest('[data-product]');
    const name = card?.querySelector('.card__title, .pdp__title')?.textContent.trim() || '';
    const size = card?.querySelector('[data-variant-picker] input:checked')?.dataset.title;
    pop.querySelector('[data-added-name]').textContent = size ? `${name} · ${size.toLowerCase()}` : name;
    const thumb = pop.querySelector('[data-added-thumb]');
    const src = photoOf(e.detail.stepper);
    thumb.replaceChildren();
    if (src) { const img = new Image(); img.src = src; img.alt = ''; thumb.append(img); }
    thumb.hidden = !src;
    const wasHidden = pop.hidden;
    pop.classList.remove('is-leaving');
    pop.hidden = false;
    if (wasHidden) motion(pop, [{ opacity: 0, transform: 'translateY(-8px) scale(0.98)' }, { opacity: 1, transform: 'none' }], { duration: 260 });
    armPop();
  });
  pop.addEventListener('pointerenter', () => clearTimeout(popTimer));
  pop.addEventListener('pointerleave', armPop);
  pop.addEventListener('focusin', () => clearTimeout(popTimer));
  pop.addEventListener('focusout', armPop);
  pop.addEventListener('click', (e) => { if (e.target.closest('[data-open-cart], [data-close-pop]')) hidePop(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') hidePop(); });
}

// Phones: the order bar tucks away over the footer, so it never covers the links there.
{
  const bar = document.querySelector('[data-order-bar]');
  const foot = document.querySelector('.shopify-section-group-footer-group, footer');
  if (bar && foot && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => bar.classList.toggle('is-tucked', entry.isIntersecting), { rootMargin: '0px 0px -40px 0px' }).observe(foot);
  }
}

/* ------------------------------------------------------------ interactions */

document.addEventListener('click', (e) => {
  const t = e.target.closest('[data-action], [data-open-cart], [data-close-cart], [data-open-sheet], [data-close-sheet], [data-set-line]');
  if (!t) return;

  if (t.hasAttribute('data-open-cart')) { e.preventDefault(); openCart(t); return; }
  if (t.hasAttribute('data-close-cart')) { e.preventDefault(); closeCart(); return; }
  if (t.hasAttribute('data-open-sheet')) { e.preventDefault(); document.getElementById(t.dataset.openSheet)?.showModal(); return; }
  if (t.hasAttribute('data-close-sheet')) { e.preventDefault(); closeAnimated(t.closest('dialog')); return; }
  if (t.hasAttribute('data-set-line')) { e.preventDefault(); Cart.setLine(t.dataset.setLine, Number(t.dataset.qty || 0)); return; }

  const stepper = t.closest('[data-stepper]');
  if (!stepper) return;
  e.preventDefault();
  const qty = Number(stepper.dataset.qty || 0);
  const step = Number(stepper.dataset.step || 1);
  const variantId = stepper.dataset.variantId;
  const action = t.dataset.action;
  // No sheet pops open on Add: the order bar (phones) or the "added" note (desktop) says it landed.
  if (action === 'add' || action === 'inc') Cart.setQuantity(variantId, qty + step, stepper);
  if (action === 'dec') Cart.setQuantity(variantId, Math.max(0, qty - step), stepper);
  if (action === 'remove') Cart.setQuantity(variantId, 0, stepper);
});

// Product page form (Enter in the form, or the no-JS button if JS arrives late): same path.
document.addEventListener('submit', (e) => {
  const form = e.target.closest('form[data-product-form]');
  if (!form) return;
  e.preventDefault();
  const id = form.querySelector('[name="id"]')?.value;
  const qty = Number(form.querySelector('[name="quantity"]')?.value || 1);
  const stepper = form.querySelector('[data-stepper]');
  const current = Cart.qty(id);
  Cart.setQuantity(id, current + qty, stepper);
});

// Checkout: re-check the window first; the hard gate is Shopify's stock, this is the courtesy.
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-checkout]');
  if (!btn) return;
  const st = currentState();
  if (st.state !== 'open' && !data.designMode) {
    e.preventDefault();
    applyState();
    announce(S.closed.replace('{{ time }}', st.nextOpenAt ? fmtWeekdayTime(st.nextOpenAt, S) : ''));
    return;
  }
  // Slots on: no checkout without a pick-up time (without JS the radios are `required`)
  const slots = btn.form?.querySelector('[data-pickup-slots]');
  if (slots && !slots.querySelector('[data-slot]:checked') && !data.designMode) {
    e.preventDefault();
    const msg = btn.form.querySelector('[data-slot-needed]');
    if (msg) { msg.hidden = false; btn.setAttribute('aria-describedby', msg.id); }
    announce(S.pickSlot);
    const first = slots.querySelector('[data-slot]');
    first?.closest('.pickup-card')?.scrollIntoView({ block: 'center', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    first?.focus({ preventScroll: true });
    return;
  }
  btn.classList.add('is-busy');
  btn.setAttribute('aria-busy', 'true');
  if (!Cart.idle()) {
    // a quantity change is still on its way: go once it has landed
    e.preventDefault();
    const go = () => {
      if (!Cart.idle()) { setTimeout(go, 100); return; }
      const fresh = document.querySelector(`${btn.closest('dialog') ? '#cart-dialog ' : '[data-cart-page] '}[data-checkout]`);
      if (fresh && !fresh.disabled) { fresh.classList.add('is-busy'); fresh.form.requestSubmit(fresh); } else btn.classList.remove('is-busy');
    };
    go();
  }
});
// Back from the checkout (bfcache): the button is a button again, and the cart may have changed.
addEventListener('pageshow', (e) => {
  document.querySelectorAll('[data-checkout].is-busy').forEach((b) => { b.classList.remove('is-busy'); b.removeAttribute('aria-busy'); });
  // Back from a page opened in a sheet: the sheet is closed, not left half-open
  if (e.persisted) {
    document.querySelectorAll('dialog.modal-sheet').forEach((d) => {
      if (!d.matches(':modal')) return;
      d.classList.remove('is-closing', 'is-dragging');
      d.close();
      d.firstElementChild?.style.removeProperty('transform');
      d.style.removeProperty('--drag-fade');
    });
  }
  if (e.persisted) syncClock();
});

// Other sheets (info): Escape and a tap on the backdrop close them with the same motion; a
// link inside starts the close while the next page loads (no sheet frozen on screen).
document.querySelectorAll('dialog.sheet').forEach((d) => {
  d.addEventListener('cancel', (e) => { e.preventDefault(); closeAnimated(d); });
  d.addEventListener('click', (e) => {
    if (e.target === d) closeAnimated(d);
    else if (e.target.closest('a[href]') && !e.defaultPrevented && !e.metaKey && !e.ctrlKey && !e.shiftKey) closeAnimated(d);
  });
});

/*
  Phones: pull a sheet down to close it. The sheet follows the finger and the backdrop thins
  with it; on release it leaves (past 30 % of its height, or a flick) or settles back. The cart
  only lets go of its own scrolling when its list is at the top.
*/
const phone = matchMedia('(max-width: 699px)');
function wireSheetDrag(d) {
  const inner = d.firstElementChild;
  if (!inner) return;
  let drag = false; // false: not ours · null: deciding · true: dragging
  let x0 = 0;
  let y0 = 0;
  let dy = 0;
  let trail = []; // [time, y] over the last 100 ms: the speed at release
  const place = (y) => {
    inner.style.transform = `translateY(${y}px)`;
    d.style.setProperty('--drag-fade', String(Math.max(0, 1 - y / inner.offsetHeight)));
  };
  inner.addEventListener('touchstart', (e) => {
    drag = phone.matches && d.matches(':modal') && !d.classList.contains('is-closing') && e.touches.length === 1 ? null : false;
    if (drag === false) return;
    x0 = e.touches[0].clientX;
    y0 = e.touches[0].clientY;
    dy = 0;
    trail = [];
  }, { passive: true });
  inner.addEventListener('touchmove', (e) => {
    if (drag === false) return;
    const t = e.touches[0];
    if (drag === null) {
      const ddx = t.clientX - x0;
      const ddy = t.clientY - y0;
      if (Math.abs(ddx) < 4 && Math.abs(ddy) < 4) return;
      drag = ddy > 0 && ddy > Math.abs(ddx) && inner.scrollTop <= 0;
      if (!drag) return;
      inner.getAnimations().forEach((a) => a.finish()); // still arriving: land it first
      d.classList.add('is-dragging');
      y0 = t.clientY;
    }
    e.preventDefault();
    dy = Math.max(0, t.clientY - y0);
    trail.push([e.timeStamp, t.clientY]);
    while (trail.length > 2 && e.timeStamp - trail[0][0] > 100) trail.shift();
    place(dy);
  }, { passive: false });
  const release = () => {
    if (!drag) { drag = false; return; }
    drag = false;
    d.classList.remove('is-dragging');
    const [first, last] = [trail[0], trail[trail.length - 1]];
    const v = first && last[0] > first[0] ? (last[1] - first[1]) / (last[0] - first[0]) : 0; // px/ms
    if (dy > inner.offsetHeight * 0.3 || (v > 0.5 && dy > 24)) { closeAnimated(d); return; }
    const from = inner.style.transform;
    inner.style.removeProperty('transform');
    d.style.removeProperty('--drag-fade');
    motion(inner, [{ transform: from }, { transform: 'none' }], { duration: 260 });
  };
  inner.addEventListener('touchend', release);
  inner.addEventListener('touchcancel', release);
}
document.querySelectorAll('dialog.modal-sheet').forEach(wireSheetDrag);

// FAQ: browsers that can't animate to auto height (Safari, Firefox) get the same glide here.
// The <details> itself grows or shrinks; a second tap mid-glide turns it around.
if (!CSS.supports('interpolate-size', 'allow-keywords')) {
  document.addEventListener('click', (e) => {
    const summary = e.target.closest('.faq__q');
    const item = summary?.parentElement;
    const body = item?.querySelector('.faq__a');
    if (!body || reduceMotion.matches || !item.animate) return;
    e.preventDefault();
    const closing = item.open && !item.classList.contains('is-shutting');
    const from = item.getBoundingClientRect().height;
    const fade = item.open ? Number(getComputedStyle(body).opacity) : 0;
    item.getAnimations().forEach((a) => a.cancel());
    body.getAnimations().forEach((a) => a.cancel());
    item.classList.toggle('is-shutting', closing);
    item.open = true;
    const shut = summary.offsetHeight + item.offsetHeight - item.clientHeight;
    const to = closing ? shut : shut + body.offsetHeight;
    const opts = { duration: 340, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)', fill: 'forwards' };
    item.style.overflow = 'hidden';
    const a = item.animate([{ height: `${from}px` }, { height: `${to}px` }], opts);
    body.animate([{ opacity: fade }, { opacity: closing ? 0 : 1 }], { ...opts, duration: closing ? 200 : 340 });
    a.finished.then(() => {
      if (closing) item.open = false;
      item.classList.remove('is-shutting');
      item.style.removeProperty('overflow');
      item.getAnimations().forEach((x) => x.cancel());
      body.getAnimations().forEach((x) => x.cancel());
    }, () => {});
  });
}

// iOS Safari only shows :active press states when a touch listener exists.
document.addEventListener('touchstart', () => {}, { passive: true });

// Photo strips (two-photo cards on phones, the product page gallery): the dots follow the swipe.
function wireDots(root = document) {
  root.querySelectorAll('[data-swipe]').forEach((strip) => {
    if (strip.dataset.dotsWired) return;
    const dots = strip.closest('.card__media, .pdp__gallery')?.querySelectorAll('[data-dots] i');
    if (!dots || dots.length < 2) return;
    strip.dataset.dotsWired = 'true';
    strip.addEventListener('scroll', () => {
      const photos = [...strip.children]; // live: a size change swaps a card's photos
      const x = strip.scrollLeft;
      let idx = 0;
      photos.forEach((p, i) => { if (Math.abs(p.offsetLeft - strip.offsetLeft - x) < Math.abs(photos[idx].offsetLeft - strip.offsetLeft - x)) idx = i; });
      dots.forEach((d, i) => d.classList.toggle('is-on', i === idx));
    }, { passive: true });
  });
}
wireDots();

// Doors intro: the overlay is CSS-only; this tidies it away (the head script has a fallback).
function endDoors() {
  if (!doc.dataset.doors) return;
  delete doc.dataset.doors;
  document.querySelector('.doors')?.remove();
}
{
  const doors = document.querySelector('.doors');
  if (doors && doc.dataset.doors) {
    doors.lastElementChild?.addEventListener('animationend', (e) => { if (e.animationName === 'doors-fade') endDoors(); });
    setTimeout(endDoors, 1800);
  }
}

document.addEventListener('visibilitychange', () => { if (!document.hidden) syncClock(); });

// Shopify theme editor: re-run after a section re-renders.
document.addEventListener('shopify:section:load', (e) => { applyState(); Cart.reflect(); layoutDialog(); wireDots(e.target); });

applyState();
syncClock();
